import React from 'react';
import {Phone, PhoneIncoming, PhoneMissed, PhoneOutgoing} from 'lucide-react';
import {CallTimelineEntry} from '../../utilis/callTimeline.ts';
import {callRowDetail, callRowKind, CallRowKind, callRowTitle, isMissedCallKind} from '../../utilis/callRow.ts';

interface CallNotificationProps {
    entry: CallTimelineEntry;
    /** Ringing the peer back. Omitted when the room cannot be called. */
    onCallBack?: () => void;
}

const KIND_ICONS: Record<CallRowKind, typeof Phone> = {
    'incoming-answered': PhoneIncoming,
    'outgoing-answered': PhoneOutgoing,
    'incoming-missed': PhoneMissed,
    // A call I placed that nobody took is still mine — the arrow says whose
    // it was, and the red says how it ended.
    'outgoing-missed': PhoneOutgoing,
    'incoming-declined': PhoneIncoming,
    'outgoing-declined': PhoneOutgoing,
    'incoming-unknown': PhoneIncoming,
    'outgoing-unknown': PhoneOutgoing,
    ringing: Phone,
};

/**
 * A call in the timeline, drawn the way a message is. The missed variant is
 * the loud one: the agent has to be able to pick it out of a scrolled-back
 * conversation without reading it (design variant 3).
 */
export const CallNotification: React.FC<CallNotificationProps> = ({entry, onCallBack}) => {
    const kind = callRowKind(entry.direction, entry.outcome);
    const isMissed = isMissedCallKind(kind);
    const Icon = KIND_ICONS[kind] ?? Phone;
    const canCallBack = !!onCallBack;

    return (
        <div className="flex px-2 py-2">
            <button
                type="button"
                data-testid="chat.message.call-notification"
                data-call-kind={kind}
                onClick={onCallBack}
                disabled={!canCallBack}
                aria-label={`${callRowTitle(kind)}. ${callRowDetail(kind, entry.startedAt, entry.durationMs)}`}
                className={`inline-flex items-center gap-3 max-w-[400px] rounded-xl px-4 py-3.5 text-left transition-colors ${
                    canCallBack ? 'cursor-pointer' : 'cursor-default'
                } ${
                    isMissed
                        ? 'bg-red-50 border border-red-100 hover:bg-red-100'
                        : 'bg-surface-muted border border-transparent hover:bg-line'
                }`}
            >
                <span
                    className={`flex items-center justify-center w-9 h-9 shrink-0 rounded-full ${
                        isMissed ? 'bg-red-100' : 'bg-canvas'
                    }`}
                >
                    {/* `!p-0`/`!text-*`: the global `button .lucide` rule pads icons
                        by 6px (shrinking them) and forces their colour. */}
                    <Icon size={20} aria-hidden="true" className={`!p-0 ${isMissed ? '!text-danger-ink' : '!text-ink-secondary'}`}/>
                </span>

                <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium leading-[18px] text-ink">
                        {callRowTitle(kind)}
                    </span>
                    <span className="block mt-0.5 text-footnote leading-[18px] text-ink-muted">
                        {callRowDetail(kind, entry.startedAt, entry.durationMs)}
                    </span>
                </span>

                {canCallBack && <Phone size={18} aria-hidden="true" className="!p-0 shrink-0 !text-ink-muted"/>}
            </button>
        </div>
    );
};

export default CallNotification;
